import { yupResolver } from '@hookform/resolvers/yup'
import { map } from 'lodash-es'
import { useCallback, useEffect, useState } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import * as yup from 'yup'

import { Header, PageTitle } from '@/components'
import { Button, Card, Checkbox, Input } from '@/components/ui'
import { PlusSquare, XCircle } from 'lucide-react'
import Loader from '../../../../common/components/Loader'
import { UserGroupsDetails } from '../../../../common/models'
import { UserGroupsDetailsPayload } from '../../../../common/models/settings'
import FormGroup from '../../../../core/components/FormGroup'
import { useCheckPermission } from '../../../../core/hooks/use-check-permission'
import {
  createUserGroup,
  getPermissionsPreset,
  getUserGroup,
  updateUserGroup,
} from '../../../../core/services/permissions'
import { MessageBox } from '../../messages/MessageBox'
import { CreateUserPermissions } from './CreateUserPermissions'

export interface UserGroupDetailsValues {
  id?: string | null
  name: string
  active: boolean
  permissionCategories: {
    id?: string | null
    name: string
    ordinal: number
    permissions: {
      id?: string | null
      name: string
      ordinal: number
      isDefault: boolean
      view: boolean
      edit: boolean
      type: string
    }[]
  }[]
}

export const CreateUserGroupPage: React.FC = () => {
  const [userGroups, setUserGroups] = useState<UserGroupsDetails>()
  const { hasEditPermission } = useCheckPermission({
    page: 'PERMISSIONS_EDIT_USER_GROUPS',
  })

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const { festivalName, editionId, groupId } = useParams()
  const navigate = useNavigate()

  const [isError, setIsError] = useState<boolean>(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')

  const getData = useCallback(async () => {
    // prettier-ignore
    (async () => {
      try {
        setIsLoading(true)
        if (!!groupId && groupId !== 'undefined') {
          const response = await getUserGroup(groupId)

          const permissionsPresetResponse = await getPermissionsPreset()

          setUserGroups(response.data)

          if (response.data) {
            setValue('id', response.data.id)
            setValue('name', response.data.name)
            setValue('active', response.data.active)
            setValue('permissionCategories', response.data.permissionCategories.map(one => {
              one.permissions = one.permissions.map(per => {
                if (!per.type) {
                  per.type = permissionsPresetResponse.data.find(p => p.name === one.name)?.permissions.find(pre => {
                    return pre.name === per.name
                  })?.type || ''
                }
                return per
              });
              return one
            }))
          }
        } else {
          const response = await getPermissionsPreset()
          if (response.data) {
            setValue('permissionCategories', response.data)
          }
        }
      } catch (e) {
        console.error(e)
      } finally {
        setIsLoading(false)
      }
    })()
  }, [editionId, festivalName])

  useEffect(() => {
    getData()
  }, [getData])

  const schema = yup.object().shape({
    id: yup.string().optional().nullable(),
    name: yup.string().required(),
    active: yup.boolean().required(),
    permissionCategories: yup
      .array()
      .of(
        yup.object().shape({
          id: yup.string().optional().nullable(),
          name: yup.string().required(),
          ordinal: yup.number().required(),
          permissions: yup
            .array()
            .of(
              yup.object().shape({
                id: yup.string().optional().nullable(),
                name: yup.string().required(),
                ordinal: yup.number().required(),
                isDefault: yup.boolean().required(),
                view: yup.boolean().required(),
                edit: yup.boolean().required(),
                type: yup.string().required(),
              }),
            )
            .required(),
        }),
      )
      .required(),
  })
  const {
    handleSubmit,
    register,
    control,
    watch,
    formState: { errors },
    setValue,
  } = useForm<UserGroupDetailsValues>({
    mode: 'onChange',
    resolver: yupResolver<UserGroupDetailsValues>(schema),
  })

  const { fields: categoryFields } = useFieldArray({
    control, // control props comes from useForm (optional: if you are using FormContext)
    name: 'permissionCategories', // unique name for your Field Array
  })

  const submit = async (values: UserGroupDetailsValues) => {
    if (!!groupId && groupId !== 'undefined') {
      const data: UserGroupsDetailsPayload = {
        name: values.name,
        active: values.active,
        permissionCategories: map(values.permissionCategories, (value) => {
          return {
            id: value.id || '',
            permissions: map(value.permissions, (v) => {
              return {
                id: v.id || '',
                view: v.view,
                edit: v.edit,
                type: v.type,
              }
            }),
          }
        }),
      }
      try {
        setIsLoading(true)
        const { status } = await updateUserGroup(groupId, data)
        if (status === 200) {
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Update Successful')
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus(
            (e as { response?: { status?: number } })?.response?.status,
          )
        } else {
          console.error(e)
        }
      } finally {
        setIsLoading(false)
      }
    } else {
      try {
        setIsLoading(true)
        const data: UserGroupsDetails = {
          name: values.name,
          active: values.active,
          permissionCategories: map(values.permissionCategories, (value) => {
            return {
              id: null,
              name: value.name,
              ordinal: value.ordinal,
              permissions: map(value.permissions, (v) => {
                return {
                  id: null,
                  name: v.name,
                  ordinal: v.ordinal,
                  isDefault: v.isDefault,
                  view: v.view,
                  edit: v.edit,
                  type: v.type,
                }
              }),
            }
          }),
        }
        const response = await createUserGroup(data)
        if (response.status === 200) {
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Create Successful')
          navigate(
            `/settings/permissions/user-groups/${festivalName}/${editionId}/${response.data.id}`,
          )
          setUserGroups(response.data)
          setValue('id', response.data.id)
          setValue('name', response.data.name)
          setValue('active', response.data.active)
          setTimeout(() => {
            map(
              response.data.permissionCategories,
              (permissionCategory, index) => {
                setValue(
                  `permissionCategories.${index}.id`,
                  permissionCategory.id,
                )
                setValue(
                  `permissionCategories.${index}.name`,
                  permissionCategory.name,
                )
                setValue(
                  `permissionCategories.${index}.ordinal`,
                  permissionCategory.ordinal,
                )
                map(permissionCategory.permissions, (permission, i) => {
                  setValue(
                    `permissionCategories.${index}.permissions.${i}.id`,
                    permission.id,
                  )
                  setValue(
                    `permissionCategories.${index}.permissions.${i}.name`,
                    permission.name,
                  )
                  setValue(
                    `permissionCategories.${index}.permissions.${i}.ordinal`,
                    permission.ordinal,
                  )
                  setValue(
                    `permissionCategories.${index}.permissions.${i}.isDefault`,
                    permission.isDefault,
                  )
                  setValue(
                    `permissionCategories.${index}.permissions.${i}.view`,
                    permission.view,
                  )
                  setValue(
                    `permissionCategories.${index}.permissions.${i}.edit`,
                    permission.edit,
                  )
                })
              },
            )
          }, 300)
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus(
            (e as { response?: { status?: number } })?.response?.status,
          )
        } else {
          console.error(e)
        }
      } finally {
        setIsLoading(false)
      }
    }
  }

  const handleCancel = async () => {
    if (userGroups) {
      setValue('id', userGroups.id)
      setValue('name', userGroups.name)
      setValue('active', userGroups.active)
      setTimeout(() => {
        map(userGroups.permissionCategories, (permissionCategory, index) => {
          setValue(`permissionCategories.${index}.id`, permissionCategory.id)
          setValue(
            `permissionCategories.${index}.name`,
            permissionCategory.name,
          )
          setValue(
            `permissionCategories.${index}.ordinal`,
            permissionCategory.ordinal,
          )
          map(permissionCategory.permissions, (permission, i) => {
            setValue(
              `permissionCategories.${index}.permissions.${i}.id`,
              permission.id,
            )
            setValue(
              `permissionCategories.${index}.permissions.${i}.name`,
              permission.name,
            )
            setValue(
              `permissionCategories.${index}.permissions.${i}.ordinal`,
              permission.ordinal,
            )
            setValue(
              `permissionCategories.${index}.permissions.${i}.isDefault`,
              permission.isDefault,
            )
            setValue(
              `permissionCategories.${index}.permissions.${i}.view`,
              permission.view,
            )
            setValue(
              `permissionCategories.${index}.permissions.${i}.edit`,
              permission.edit,
            )
            setValue(
              `permissionCategories.${index}.permissions.${i}.type`,
              permission.type,
            )
          })
        })
      }, 300)
    }
  }

  return (
    <>
      <Header pageTitle="User Groups" />
      {isLoading && <Loader />}
      <Card className="!bg-monochrome4-60 p-5 backdrop-blur-lg max-h-[calc(100vh-150px)] overflow-y-auto">
        <div className="flex items-center justify-between gap-4 mb-6">
          <PageTitle
            title={userGroups ? userGroups.name : 'Add new Group'}
            icon="users-filled"
            backButton
            backButtonFunction={() => {
              navigate(
                `/settings/permissions/user-groups/${festivalName}/${editionId}`,
              )
            }}
          />
          <div className="flex gap-2 items-center">
            <Button variant="secondary" onClick={handleCancel}>
              <XCircle className="w-4 h-4" />
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit(submit)}
              disabled={isLoading}
              loading={isLoading}
            >
              <PlusSquare className="w-4 h-4" />
              Save
            </Button>
          </div>
        </div>

        <form id="user-groups-form" onSubmit={handleSubmit(submit)}>
          <div className="flex flex-col items-start gap-4">
            <div className="flex flex-col items-start gap-2 w-full">
              <div className="flex items-center justify-between">
                <h4 className="mb-0 text-title-xxs text-accent-1 font-semibold">
                  Group Settings
                </h4>
              </div>
              <div className="flex w-full bg-monochrome-5 rounded-md border border-monochrome-120 p-4">
                <div className="flex flex-col items-start gap-3 p-3 w-full">
                  <FormGroup label="Name">
                    <Input
                      isInvalid={!!errors?.name?.message}
                      {...register('name')}
                      type="text"
                      disabled={!hasEditPermission}
                    />
                  </FormGroup>
                  <FormGroup
                    label="Active"
                    direction="row"
                    labelClassName="whitespace-nowrap"
                  >
                    <Checkbox
                      checked={watch('active')}
                      onCheckedChange={(checked) => {
                        setValue('active', Boolean(checked))
                      }}
                      disabled={!hasEditPermission}
                    />
                  </FormGroup>
                </div>
              </div>
            </div>
            <div className="flex flex-col items-start gap-4 w-full">
              {map(categoryFields, (field, index) => {
                return (
                  <div key={index} className="flex flex-col w-full gap-2">
                    <div className="flex items-center justify-between">
                      <h4 className="text-title-xs font-semibold">
                        {field.name}
                      </h4>
                    </div>
                    <CreateUserPermissions
                      nestIndex={index}
                      {...{ control, register, errors, watch, setValue }}
                      disabled={!hasEditPermission}
                    />
                  </div>
                )
              })}
            </div>
          </div>
        </form>
      </Card>
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
    </>
  )
}
